import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { t } from '../../lib/i18n';
import { safeNextPath, signInAdmin } from './session';

/**
 * «Вхід для адміністраторів». Sign-in only: there is no registration route
 * anywhere in this SPA, and the API refuses every account without the admin
 * role with the same message it gives a wrong password.
 */
export function LoginPage() {
  const search = useSearch({ strict: false }) as { next?: string; reason?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(
    search.reason === 'not-admin' ? t('admin.login.notAdmin') : null,
  );

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const result = await signInAdmin(email.trim(), password);
    setSubmitting(false);

    if (result.kind === 'ok') {
      // Whatever was cached belonged to the previous session (or to none).
      queryClient.clear();
      await navigate({ to: safeNextPath(search.next) });
      return;
    }
    setPassword('');
    if (result.kind === 'invalid') setError(t('admin.login.invalid'));
    else if (result.kind === 'throttled')
      setError(t('admin.login.throttled', { minutes: Math.max(1, Math.ceil(result.retryAfterSec / 60)) }));
    else setError(t('admin.login.error'));
  };

  const field =
    'h-11 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[15px] text-cf-ink outline-none focus:border-cf-ink';
  const label = 'flex flex-col gap-1.5 text-[11px] font-mono tracking-[0.1em] text-cf-ink-subtle uppercase';

  return (
    <main className="flex min-h-screen items-center justify-center bg-cf-ground px-4">
      <form
        onSubmit={onSubmit}
        noValidate
        aria-describedby={error ? 'admin-login-error' : undefined}
        className="w-full max-w-[380px] rounded-cf-lg border border-cf-line bg-cf-surface p-6"
      >
        <img src="/assets/cf/logo.png" alt="Cars Finders" className="mb-5 h-[30px] w-auto" />
        <h1 className="mb-1 text-[22px] font-semibold text-cf-ink">{t('admin.login.title')}</h1>
        <p className="mb-5 text-[13px] text-cf-ink-subtle">{t('admin.login.subtitle')}</p>

        <div className="flex flex-col gap-3">
          <label className={label}>
            {t('admin.login.email')}
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={`${field} normal-case tracking-normal`}
            />
          </label>
          <label className={label}>
            {t('admin.login.password')}
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${field} normal-case tracking-normal`}
            />
          </label>
        </div>

        {error && (
          <p id="admin-login-error" role="alert" className="mt-3 text-[13px] text-cf-danger">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !email.trim() || !password}
          className="mt-5 h-11 w-full cursor-pointer rounded-cf-md bg-cf-brand text-[15px] font-semibold text-white hover:bg-cf-brand-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t('admin.login.submitting') : t('admin.login.submit')}
        </button>
      </form>
    </main>
  );
}
